import axios from 'axios';
import { actions, AsyncAction } from '..';
import { planFactory } from '../../types/factory';
import { DamageMechanism, InspectionActivity, Plan, RiskAssessment, ThicknessRequirement } from '../../types/plan';
import { uploadPicture } from '../../util/file';

type State = {
    fetching: boolean;
    status: 'idle' | 'updating' | 'success' | 'fail';
    plan?: Plan;
    changed?: Plan;
    changes: { date: Date; change: Partial<Plan> }[];
    error?: Error;
};

type Action =
    | { type: 'plan/reset' }
    | { type: 'plan/status'; payload: { status: State['status'] } }
    | { type: 'plan/set'; payload?: { plan: Plan }; error?: Error }
    | { type: 'plan/edit'; payload: { change: Partial<Plan> } }
    | { type: 'plan/commit' }
    | { type: 'plan/discard'; payload: { all: boolean } };

const initialState: State = {
    fetching: false,
    status: 'idle',
    changes: [],
};

/**
 * Apply `changes` to a `plan` object returning a new object.
 *
 * @param plan the base plan
 * @param changes changes to be applied
 * @returns updated object
 */
const withChanges = <T extends Plan | Partial<Plan>>(plan?: T, changes?: Partial<Plan>) =>
    ({ ...plan, ...changes } as T);

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'plan/reset': {
            return { ...initialState };
        }
        case 'plan/status': {
            return { ...state, ...action.payload };
        }
        case 'plan/set': {
            if (action.payload)
                return { ...state, fetching: false, ...action.payload, changed: action.payload.plan, changes: [] };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        case 'plan/edit': {
            if (!state.plan) return { ...state, error: Error('cannot edit undefined plan') };
            const changes = [...state.changes, { date: new Date(), ...action.payload }];
            const changed = changes.reduce((acc, c) => withChanges(acc, c.change), state.plan);
            return { ...state, changes, changed };
        }
        case 'plan/commit': {
            if (!state.plan) return { ...state, error: Error('cannot commit undefined plan') };
            return { ...state, plan: state.changed, changes: [] };
        }
        case 'plan/discard': {
            if (!state.plan) return { ...state, error: Error('cannot discard undefined plan') };
            if (action.payload.all) state.changes = [];
            else state.changes.pop();
            const changed = state.changes.reduce((acc, c) => withChanges(acc, c.change), state.plan);
            return { ...state, changed };
        }
        default:
            return state;
    }
};

/**
 * Reset the reducer to the initial state.
 */
const reset = (): Action => ({ type: 'plan/reset' });

/**
 * Create a new plan locally using the asset contained in the asset store.
 *
 * @param rethrow throw the internal error
 */
const create = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { asset } = getState();
    try {
        dispatch({ type: 'plan/status', payload: { status: 'idle' } });
        dispatch({ type: 'plan/set' });
        dispatch(actions.planProjects.reset());
        if (!asset.asset) throw new Error('asset store does not contain an asset');
        dispatch({ type: 'plan/set', payload: { plan: planFactory(asset.asset.code) } });
    } catch (error) {
        dispatch({ type: 'plan/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Fetch an existing plan from the back-end.
 *
 * @param id plan id
 * @param rethrow throw the internal error
 */
const get = (id: number, rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'plan/status', payload: { status: 'idle' } });
        dispatch({ type: 'plan/set' });
        dispatch(actions.planProjects.reset());
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'plan/set': cancel } });
        const response = await axios.get<Plan>(`/inspection-plans/${id}/`, { cancelToken: cancel.token });
        dispatch({ type: 'plan/set', payload: { plan: response.data } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'plan/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Update the local plan to the backend.
 *
 * @param rethrow throw the internal error
 */
const update = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { plan } = getState();
    try {
        dispatch({ type: 'plan/status', payload: { status: 'updating' } });
        dispatch({ type: 'plan/set' });
        if (!plan.plan) throw Error('plan not available in redux store');
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'plan/set': cancel } });
        const action = plan.plan.id === -1 ? axios.post : axios.put;
        const url = plan.plan.id === -1 ? '/inspection-plans/' : `/inspection-plans/${plan.plan.id}/`;
        const blueprint_damages = await uploadPictures(plan.plan.blueprint_damages);
        const response = await action<Plan>(
            url,
            {
                ...plan.plan,
                blueprint_damages,
                thickness_requirement: undefined,
                risk_assessment: undefined,
                inspection_activity: undefined,
            },
            { cancelToken: cancel.token },
        );
        const createdPlan: Plan = {
            ...response.data,
            thickness_requirement: plan.plan.thickness_requirement,
            risk_assessment: plan.plan.risk_assessment,
            inspection_activity: plan.plan.inspection_activity,
            damage_mechanism: plan.plan.damage_mechanism,
        };
        const updatedPlan = await updateRelationships(createdPlan);
        dispatch({ type: 'plan/set', payload: { plan: updatedPlan } });
        dispatch({ type: 'plan/status', payload: { status: 'success' } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'plan/set', error });
        dispatch({ type: 'plan/status', payload: { status: 'fail' } });
        if (rethrow) throw error;
    }
};

/**
 * Update the plan relationships.
 * The plan must already present in the backend and have a valid id.
 *
 * @param plan plan to update
 * @returns `plan` with updated relationships
 */
const updateRelationships = async (plan: Plan) => {
    // const { damage_mechanism: previousDms = [], reference_documents: previousRds = [] } = (
    //     await axios.get<Plan>(`/inspection-plans/${plan.id}/`)
    // ).data;
    const relationshipPromises = [
        {
            relationship: plan.thickness_requirement,
            route: 'thickness-requirements',
            before: undefined,
            after: (value: ThicknessRequirement) => (plan.thickness_requirement = value),
        },
        {
            relationship: plan.risk_assessment,
            route: 'risk-assessments',
            before: undefined,
            after: (value: RiskAssessment) => (plan.risk_assessment = value),
        },
        ...(plan.inspection_activity ?? []).map((activity, i) => ({
            relationship: activity,
            route: 'inspection-activities',
            before: (value: InspectionActivity) => (value.inspection_plan = plan.id),
            after: (value: InspectionActivity) => plan.inspection_activity?.splice(i, 1, value),
        })),
        ...(plan.damage_mechanism ?? []).map((damageMechanism, i) => ({
            relationship: damageMechanism,
            route: 'damage-mechanisms',
            before: (value: DamageMechanism) => (value.inspection_plan = { id: plan.id } as any),
            after: (value: DamageMechanism) => plan.damage_mechanism?.splice(i, 1, value),
        })),
        // document references are many to many relationships, handled automatically
    ].map(async ({ relationship, route, before, after }) => {
        if (!relationship) return;
        const update = relationship.id !== -1;
        const url = `/${route}/${update ? relationship.id : ''}${update ? '/' : ''}`;
        const action = update ? axios.put : axios.post;
        before?.(relationship as any);
        const result = await action(url, relationship);
        after?.(result.data as any);
    });
    plan.blueprint_damages = await uploadPictures(plan.blueprint_damages);
    await Promise.all(relationshipPromises);
    const previousPlan = (await axios.get<Plan>(`/inspection-plans/${plan.id}/`)).data;
    const damageMechanisms = plan.damage_mechanism ?? [];
    await Promise.all(
        (previousPlan.damage_mechanism ?? [])
            ?.filter(previousDm => !damageMechanisms.find(dm => dm.id === previousDm.id))
            .map(dm => axios.delete(`damage-mechanisms/${dm.id}`)),
    );
    return (await axios.put<Plan>(`/inspection-plans/${plan.id}/`, plan)).data;
};

export const uploadPictures = async (pictures: string[]) =>
    (await Promise.all(pictures.map(async p => (!p.startsWith('data:') ? p : await uploadPicture(p))))) //
        .filter(blueprint => blueprint !== '');

/**
 * Update the plan information locally, but without committing it.
 *
 * @param change plan object changes
 */
const edit = (change: Partial<Plan>): Action => ({ type: 'plan/edit', payload: { change } });

/**
 * Commit the edited plan, the old plan information cannot be restored.
 */
const commit = (): Action => ({ type: 'plan/commit' });

/**
 * Discard the edited plan.
 *
 * @param all if should discard all changes, not only the last
 */
const discard = (all = false): Action => ({ type: 'plan/discard', payload: { all } });

export const creators = { reset, create, get, update, edit, commit, discard };
